import { Box, Icon, TextButton } from '@zillow/constellation-10';
import { IconChevronRightFilled, IconNoteOutline } from '@zillow/constellation-icons';
import { useContext } from 'react';
import { ModalType } from '../../types/ModalType';
import {
  emitNotesButtonClickEvent,
  NOTES_BUTTON_CLICK_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/notesModal';
import useModal from '../../hooks/useModal';
import { useConversationMessagesQuery } from '../../hooks/useConversationMessagesQuery';
import type { ConversationMessagesData } from '../../utils/conversationMessageUtils';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import StatusLabel from '../StatusLabel';
import { useAuth } from '../../AuthProvider';

export type NotesBarData = {
  statusLabelText: string;
  statusLabelLinkId: string;
  noteText: string;
  isApplicationAlreadySent: boolean;
  isApplicationMarkedSent: boolean;
  tourScheduledTimestampMs?: number;
};

export const selectNotesBarData = (data: ConversationMessagesData): NotesBarData => ({
  statusLabelText: data.statusLabel.text,
  statusLabelLinkId: data.statusLabel.linkedId,
  noteText: data.note.note ?? '',
  isApplicationAlreadySent: data.note.isApplicationAlreadySent,
  isApplicationMarkedSent: data.note.isApplicationMarkedSent,
  tourScheduledTimestampMs: data.note.tourScheduledTimestampMs,
});

const NotesBar = (): JSX.Element | null => {
  const { listingAlias, conversationId, clientTimeZone } = useContext(InboxPageContext);
  const { setActiveModal } = useModal();
  const { user } = useAuth();
  // Cache-only read — `MessageListItems` owns the `getMessages` fetch + polling.
  const { data: conversationData } = useConversationMessagesQuery({
    conversationId,
    clientTimeZone,
    enabled: false,
    select: selectNotesBarData,
  });

  if (!conversationData) {
    return null;
  }
  const {
    statusLabelText,
    statusLabelLinkId,
    noteText,
    isApplicationAlreadySent,
    isApplicationMarkedSent,
    tourScheduledTimestampMs,
  } = conversationData;

  const showStatusLabel = !!statusLabelText;
  // `!!tourScheduledTimestampMs` (not `!= null`) so a 0/epoch timestamp counts as "no tour", matching prior behavior.
  const hasNotesData = !!noteText || isApplicationAlreadySent || isApplicationMarkedSent || !!tourScheduledTimestampMs;
  const textDisplay = hasNotesData ? 'See notes' : 'Take a note';

  const handleNotesBarClick = (): void => {
    emitNotesButtonClickEvent(user, { listingAlias });

    setActiveModal(ModalType.NotesModal);
  };

  return (
    <TextButton
      data-eventid={NOTES_BUTTON_CLICK_EVENT_TYPE_ID}
      aria-haspopup="dialog"
      data-testid="notes-bar"
      onClick={handleNotesBarClick}
      tone="neutral"
      textStyle="body-sm-bold"
      icon={
        <Icon css={{ color: 'brand' }} size="md">
          <IconNoteOutline />
        </Icon>
      }
      css={{
        display: 'flex',
        justifyContent: 'flex-start',
        padding: 'tight',
        backgroundColor: 'backgroundLight',
        '& span': {
          width: '100%',
        },
      }}
    >
      <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {textDisplay}

        {showStatusLabel && (
          <Box
            css={{
              display: 'flex',
              alignItems: 'center',
              gap: 'tighter',
            }}
          >
            <StatusLabel text={statusLabelText} linkId={statusLabelLinkId} />
            <Icon css={{ color: 'icon.muted' }}>
              <IconChevronRightFilled />
            </Icon>
          </Box>
        )}
      </Box>
    </TextButton>
  );
};

export default NotesBar;
